import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use } from "react";

import { useBannerSliderContext } from "@/components/BannerSlider/context/bannerSlider.context";
import { useTimer } from "@/utils/hooks/timer/useTimer.hook";
import {
    TimerDirection,
    TimerStatus
} from "@/utils/hooks/timer/useTimer.types";

type ProgressBarContextValue = {
    startTimer: () => void;
    status: TimerStatus;
    stopTimer: () => void;
};

type ProgressBarContextProviderProps = RequireChildrenFor<{}>;

export const ProgressBarContext = createContext<
    ProgressBarContextValue | undefined
>(undefined);
ProgressBarContext.displayName = "ProgressBarContext";

export const ProgressBarContextProvider = ({
    children
}: ProgressBarContextProviderProps) => {
    const { interval, delay, slideNext } = useBannerSliderContext();

    const { start, status, stop } = useTimer({
        autoRestart: true,
        autoStart: true,
        direction: TimerDirection.Up,
        referenceTime: delay,
        interval: interval,
        onEnd: slideNext
    });

    const startTimer = () => {
        start();
    };

    const stopTimer = () => {
        stop();
    };

    return (
        <ProgressBarContext
            value={{
                startTimer,
                status,
                stopTimer
            }}
        >
            {children}
        </ProgressBarContext>
    );
};

export const useProgressBarContext = () => {
    const context = use<ProgressBarContextValue | undefined>(
        ProgressBarContext
    );

    if (!isDefined(context)) {
        throw new Error(`${ProgressBarContext.displayName} is undefined.`);
    }

    return context;
};
